import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuthStore } from '../../lib/auth';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';

// Direct Supabase Auth (GoTrue) — the Express /auth/signup backend is undeployed.
import { SUPABASE_URL, SUPABASE_KEY as SUPABASE_ANON } from '../../lib/supabaseConfig';

export function Signup() {
  const [tenantName, setTenantName] = useState('');
  const [tenantSlug, setTenantSlug] = useState('');
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const setSession = useAuthStore((s) => s.setSession);
  const nav = useNavigate();

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setNotice(null);
    try {
      // GoTrue signup. tenant provisioning (tenants + rainmaker_users rows) is
      // Lane A backend work — carry the org details in user_metadata for later.
      const res = await fetch(`${SUPABASE_URL}/auth/v1/signup`, {
        method: 'POST',
        headers: { apikey: SUPABASE_ANON, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          password,
          data: { full_name: fullName, tenant_name: tenantName, tenant_slug: tenantSlug },
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error_description ?? data.msg ?? data.error ?? 'Signup failed');

      if (data.access_token) {
        // Auto-confirm on → we have a live session.
        setSession({
          user: { id: data.user?.id ?? '', tenantId: '', role: 'director', fullName, email },
          accessToken: data.access_token,
          refreshToken: data.refresh_token,
          tenantSlug,
        });
        // New Rainmaker workspaces land in the working, SA-localised Entity Setup
        // Wizard (Supabase-backed) — not the legacy PMO project onboarding (Express).
        nav('/finance/entity-setup');
      } else {
        // Email-confirmation required → no session yet.
        setNotice('Account created. Check your email to confirm, then sign in.');
      }
    } catch (e) { setError((e as Error).message); }
  }

  return (
    <div className="min-h-screen grid place-items-center p-6">
      <div className="card w-full max-w-md">
        <h1 className="text-2xl font-bold mb-1">Create your PMO workspace</h1>
        <p className="text-sm text-slate-600 mb-4">14-day free trial. No credit card required.</p>
        <form onSubmit={submit} className="space-y-3">
          <Input label="Organisation name" value={tenantName} onChange={(e) => setTenantName(e.target.value)} required />
          <Input label="Workspace slug (lowercase, dashes)" value={tenantSlug} onChange={(e) => setTenantSlug(e.target.value)} required pattern="[a-z0-9-]+" />
          <Input label="Your full name" value={fullName} onChange={(e) => setFullName(e.target.value)} required />
          <Input label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          <Input label="Password (min 10 chars)" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={10} />
          {error ? <div className="text-sm text-red-600">{error}</div> : null}
          {notice ? <div className="text-sm text-green-700">{notice}</div> : null}
          <Button type="submit" className="w-full">Create workspace</Button>
        </form>
        <div className="mt-4 text-sm">
          <Link to="/auth/login" className="text-slate-600 hover:underline">Already have an account? Sign in</Link>
        </div>
      </div>
    </div>
  );
}
